/**
 * The wall, as a picture, derived from public counts alone.
 *
 * A real table shows 17 stacks of two tiles on each of four sides. The engine
 * does not model that geometry - it holds one ordered array plus a dead wall -
 * so this module turns the two numbers every player is already allowed to see
 * (`wallRemaining`, `deadWallRemaining`) into a physical wall that empties in
 * the right order.
 *
 * It is deliberately presentation-only:
 *
 *   - it never receives a tile, a kind, an id, or the seed;
 *   - the only private-looking input is a break point, and that is computed
 *     from the dealer, which is public in every mode including Online;
 *   - nothing it returns can be inverted into a draw order, because the tiles
 *     it describes have no identity at all.
 *
 * Because it is pure and deterministic, the same counts always draw the same
 * wall, so the picture never flickers between renders or between clients.
 */

export const WALL_SIDES = ["north", "east", "south", "west"] as const;
export type WallSideName = (typeof WALL_SIDES)[number];

export const STACKS_PER_SIDE = 17;
export const TILES_PER_STACK = 2;
export const TILES_PER_SIDE = STACKS_PER_SIDE * TILES_PER_STACK;
export const WALL_TILE_COUNT = WALL_SIDES.length * TILES_PER_SIDE;

export interface WallStack {
  /** Which of the four walls this stack belongs to. */
  side: WallSideName;
  /** 0..16, counting along the wall in draw order. */
  index: number;
  /** The tile resting on top of the stack is still there. */
  upper: boolean;
  /** The tile underneath is still there. */
  lower: boolean;
}

export interface WallSide {
  side: WallSideName;
  stacks: WallStack[];
}

export interface WallPresentation {
  sides: WallSide[];
  /** Tiles still standing anywhere on the wall, live plus dead. */
  standing: number;
  /** Tiles that have left the wall: the deal plus every draw since. */
  taken: number;
  /** Stack index, 0..16, where drawing started on the dealer's wall. */
  breakIndex: number;
  /** Which wall was broken first. */
  breakSide: WallSideName;
}

export interface WallCounts {
  /** Live wall tiles left, straight from the engine or the redacted view. */
  wallRemaining: number;
  /** Dead wall tiles left; zero in rule sets without one. */
  deadWallRemaining: number;
  /**
   * Seat that deals this hand. Public information, used only to choose where
   * the wall is broken so the picture is stable across a hand.
   */
  dealer: number;
  /**
   * The seat this screen belongs to, so the break can be drawn in front of the
   * dealer. Also public: you always know where you are sitting.
   */
  you?: number;
}

function clamp(value: number, low: number, high: number): number {
  return Math.min(high, Math.max(low, value));
}

/**
 * Where the wall opens.
 *
 * On a real table the dice decide this. This milestone's engine does not model
 * dice, so the break is a fixed function of the dealer: it lands on the
 * dealer's own wall, a few stacks in, exactly as a real break usually does.
 *
 * "The dealer's own wall" has to mean the wall drawn in front of the dealer on
 * *this* screen, not the dealer's seat number - the two are only the same for
 * the player sitting in seat 0. Taking the viewer into account is what makes
 * the gap in the wall appear where a player at the table would expect it: in
 * front of whoever is dealing, wherever that person is sitting relative to you.
 */
function breakPositionOf(dealer: number, you: number): number {
  const side = WALL_SIDES.indexOf(wallSideFor(dealer, you));
  const stack = 4 + ((dealer * 3) % 9);
  return (side < 0 ? 0 : side) * TILES_PER_SIDE + stack * TILES_PER_STACK;
}

/**
 * Build the whole wall.
 *
 * Tiles leave in draw order starting at the break: within a stack the top tile
 * goes first, then the one under it, then on to the next stack, wrapping round
 * the four walls. `wallRemaining + deadWallRemaining` tiles are left standing,
 * which is precisely what the public state says.
 */
export function presentWall({
  wallRemaining,
  deadWallRemaining,
  dealer,
  you = 0,
}: WallCounts): WallPresentation {
  const standing = clamp(
    Math.round(wallRemaining) + Math.round(deadWallRemaining),
    0,
    WALL_TILE_COUNT,
  );
  const taken = WALL_TILE_COUNT - standing;
  const start = breakPositionOf(dealer, you);

  const sides: WallSide[] = WALL_SIDES.map((side) => ({
    side,
    stacks: Array.from({ length: STACKS_PER_SIDE }, (_, index) => ({
      side,
      index,
      upper: true,
      lower: true,
    })),
  }));

  for (let step = 0; step < taken; step += 1) {
    const position = (start + step) % WALL_TILE_COUNT;
    const sideIndex = Math.floor(position / TILES_PER_SIDE);
    const within = position % TILES_PER_SIDE;
    const stack = sides[sideIndex]?.stacks[Math.floor(within / TILES_PER_STACK)];
    if (!stack) continue;
    if (within % TILES_PER_STACK === 0) stack.upper = false;
    else stack.lower = false;
  }

  return {
    sides,
    standing,
    taken,
    breakIndex: Math.floor((start % TILES_PER_SIDE) / TILES_PER_STACK),
    breakSide: WALL_SIDES[Math.floor(start / TILES_PER_SIDE)] ?? "north",
  };
}

/**
 * Where a wall is drawn, relative to the player at the bottom of the screen.
 *
 * The wall in front of a seat belongs to that seat, so the mapping is the same
 * one the seats use: subtract the viewer and wrap.
 */
export function wallSideFor(playerId: number, you: number): WallSideName {
  const offset = ((playerId - you) % WALL_SIDES.length + WALL_SIDES.length) % WALL_SIDES.length;
  return (["south", "east", "north", "west"] as const)[offset] ?? "south";
}
